import { matchesPhoneSearch } from './phoneUtils.js';

export const GRADUATION_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const GRADUATION_DEPOSIT_STATUSES = [
  'PENDING',
  'PARTIALLY_PAID',
  'PAID',
  'FORFEITED',
  'REFUNDED',
  'WAIVED'
];

export const toGraduationStudentId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value._id || value.id || '');
};

export const getGraduationStudentDisplayName = (student) =>
  student?.studentInfo?.name
  || student?.person?.name
  || student?.studentId?.companyName
  || student?.studentId?.fullName
  || 'N/A';

export const getGraduationStudentEnrollmentNumber = (student) =>
  student?.studentInfo?.enrollmentNumber
  || student?.enrollmentId?.enrollmentNumber
  || student?.person?.reference
  || '';

export const getGraduationStudentEmail = (student) =>
  student?.studentInfo?.email
  || student?.person?.email
  || student?.studentId?.email
  || '';

export const getGraduationStudentPhone = (student) =>
  student?.studentInfo?.phone
  || student?.person?.phone
  || student?.studentId?.phone
  || '';

export const getGraduationStudentPhones = (student) => [
  student?.studentInfo?.phone,
  student?.studentInfo?.alternatePhone,
  student?.studentId?.phone,
  student?.studentId?.alternatePhone,
  student?.studentId?.emergencyContact?.phone
].filter(Boolean);

export const getGraduationStudentProgram = (student) => {
  const subProduct = student?.subProductId;
  if (!subProduct || typeof subProduct !== 'object') return student?.person?.programName || 'N/A';
  const label = subProduct.name || subProduct.code;
  return subProduct.code && subProduct.name ? `${subProduct.name} (${subProduct.code})` : (label || 'N/A');
};

export const getGraduationStudentIntakeLabel = (student) => {
  const intake = student?.intakeId;
  if (!intake || typeof intake !== 'object') return student?.person?.batchName || 'N/A';
  return intake.name ? `${intake.name}${intake.code ? ` (${intake.code})` : ''}` : (intake.code || 'N/A');
};

export const getGraduationStudentEnrollmentStatus = (student) => student?.enrollmentId?.status || '';

export const getGraduationStudentSearchHaystack = (student) =>
  [
    getGraduationStudentDisplayName(student),
    getGraduationStudentEnrollmentNumber(student),
    getGraduationStudentEmail(student),
    getGraduationStudentPhone(student),
    student?.studentInfo?.alternatePhone,
    student?.studentId?.alternatePhone,
    getGraduationStudentProgram(student),
    getGraduationStudentIntakeLabel(student)
  ].filter(Boolean).join(' ').toLowerCase();

export const filterGraduationStudents = (students, filters, options = {}) => {
  const {
    search = '',
    depositStatus = '',
    subProductId = '',
    intakeId = '',
    attendanceFilter = '',
    paymentLinkFilter = '',
    enrollmentStatus = ''
  } = filters;

  const { linkEligibleOnly = false } = options;
  const term = search.trim().toLowerCase();

  return students.filter((student) => {
    if (linkEligibleOnly && ['PAID', 'REFUNDED', 'WAIVED'].includes(student.deposit?.status)) {
      return false;
    }

    if (term) {
      const textMatches = getGraduationStudentSearchHaystack(student).includes(term);
      const phones = getGraduationStudentPhones(student);
      const phoneMatches = phones.some((p) => matchesPhoneSearch(p, search));
      if (!textMatches && !phoneMatches) {
        return false;
      }
    }

    if (depositStatus) {
      const status = student.deposit?.status || 'PENDING';
      if (status !== depositStatus) return false;
    }

    if (subProductId && toGraduationStudentId(student.subProductId) !== subProductId) return false;
    if (intakeId && toGraduationStudentId(student.intakeId) !== intakeId) return false;

    if (attendanceFilter === 'confirmed' && !student.attendance?.confirmed) return false;
    if (attendanceFilter === 'not_confirmed' && student.attendance?.confirmed) return false;

    if (paymentLinkFilter === 'has_link' && !student.deposit?.paymentLink?.token && !student.deposit?.paymentLink?.url) {
      return false;
    }
    if (paymentLinkFilter === 'no_link' && (student.deposit?.paymentLink?.token || student.deposit?.paymentLink?.url)) {
      return false;
    }

    if (enrollmentStatus && getGraduationStudentEnrollmentStatus(student) !== enrollmentStatus) {
      return false;
    }

    return true;
  });
};

export const paginateList = (items, page, pageSize) => {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total,
    totalPages,
    currentPage
  };
};

export const getGraduationEnrollmentStatusOptions = (students) => {
  const statuses = new Set(students.map(getGraduationStudentEnrollmentStatus).filter(Boolean));
  return Array.from(statuses).sort();
};

export const hasActiveGraduationStudentFilters = (filters) => Boolean(
  filters.search?.trim()
  || filters.depositStatus
  || filters.subProductId
  || filters.intakeId
  || filters.attendanceFilter
  || filters.paymentLinkFilter
  || filters.enrollmentStatus
);

export const createEmptyGraduationStudentFilters = () => ({
  search: '',
  depositStatus: '',
  subProductId: '',
  subProductLabel: '',
  intakeId: '',
  intakeLabel: '',
  attendanceFilter: '',
  paymentLinkFilter: '',
  enrollmentStatus: ''
});
